import React, { useMemo, useRef } from "react";
import type { LayoutFieldType, LayoutFieldTypesType } from "@whatslly/types/LayoutFieldType";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { Field, FieldProps, FormikProps } from "formik";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import styles from "./AddEditNewRawFieldModal.module.scss";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { MultipleInputSelect } from "@whatslly/ui-components/MultipleInputSelect";
import { QuestionMarkIcon } from "@whatslly/ui-components/QuestionMarkIcon";
import { Tooltip } from "@whatslly/ui-components/Tooltip";

const MIX_MAX_SUPPORTED_FIELD_TYPES = ["int", "percent", "double", "currency"];
const DEFAULT_VALUE_SUPPORTED_FIELD_TYPES = ["int", "percent", "double", "currency", "string", "picklist"];

const FIELD_TYPES: LayoutFieldTypesType[] = [
  "string",
  "email",
  "phone",
  "textarea",
  "boolean",
  "checkbox",
  "date",
  "picklist",
  "multipicklist",
  "datetime",
  "time",
  "currency",
  "double",
  "int",
  "percent",
  "url",
  "address",
  "reference",
];

const FIELD_TYPE_OPTIONS = FIELD_TYPES.map((fieldType) => ({ label: fieldType, value: fieldType }));

const INIT_VALUE = {
  label: "",
  api: "",
  type: null as unknown as { label: string; value: LayoutFieldTypesType } | undefined | null,
  required: false,
  deletable: true,
  refApi: "",
  refDependsOn: "",
  refOrderField: "CreatedDate",
  refSearchField: "Name",
  refLookupFieldId: "Id",
  refLookupFilter: "",
  relationshipName: "",
  relationshipFieldName: "Name",
  defaultValue: undefined,
  minValue: undefined,
  maxValue: undefined,
};

type FormFiledType = Omit<typeof INIT_VALUE, "defaultValue" | "minValue" | "maxValue"> & {
  defaultValue: undefined | number | string;
  minValue: undefined | number;
  maxValue: undefined | number;
};

const fieldObjToInit = (field: LayoutFieldType): FormFiledType => {
  return {
    label: field.l,
    api: field.a,
    type: { label: field.t, value: field.t },
    required: field.r,
    deletable: field.d,
    refApi: field.ref?.apiName ?? INIT_VALUE.refApi,
    refDependsOn: field.ref?.dependsOn ? field.ref.dependsOn.join(",") : INIT_VALUE.refDependsOn,
    refOrderField: field.ref?.orderField ?? INIT_VALUE.refOrderField,
    refSearchField: field.ref?.searchField ?? INIT_VALUE.refSearchField,
    refLookupFieldId: field.ref?.lookupFieldId ?? INIT_VALUE.refLookupFieldId,
    refLookupFilter: field.ref?.lookupFilter ?? INIT_VALUE.refLookupFilter,
    relationshipName: field.relationshipName ?? INIT_VALUE.relationshipName,
    relationshipFieldName: field.relationshipFieldName ?? INIT_VALUE.relationshipFieldName,
    defaultValue: field.defaultValue,
    minValue: field.minValue,
    maxValue: field.maxValue,
  };
};

export const AddEditNewRawFieldModal: React.FC<
  { field?: LayoutFieldType; isCreate: boolean } & IModalInstance<{
    field: LayoutFieldType;
  }>
> = (props) => {
  const formikContext = useRef<FormikProps<FormFiledType>>(null);

  const initialFormState = useMemo(() => {
    return props.field ? fieldObjToInit(props.field) : INIT_VALUE;
  }, [props.field]);

  return (
    <BaseModal
      className={styles.modal}
      bodyClassName={styles.body}
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={() => props.closeModal()}
      header={<ModalHeader closeModal={() => props.closeModal()}></ModalHeader>}
    >
      <FormElement
        formikRef={formikContext}
        initialValues={initialFormState}
        onSubmit={(values) => {
          const ref = (() => {
            if (values.type?.value === "reference" && props.isCreate) {
              return {
                dependsOn: values.refDependsOn?.split(",") ?? [],
                apiName: values.refApi,
                orderField: values.refOrderField,
                searchField: values.refSearchField,
                lookupFieldId: values.refLookupFieldId,
                lookupFilter: values.refLookupFilter,
              };
            }
            return;
          })();
          const field: LayoutFieldType = {
            l: values.label,
            a: values.api,
            t: values.type?.value ?? `string`,
            r: values.required,
            d: values.deletable,
            ref,
          };

          if (MIX_MAX_SUPPORTED_FIELD_TYPES.includes(values.type?.value ?? "")) {
            if (values.minValue) {
              field.minValue = Number(values.minValue);
            }
            if (values.maxValue) {
              field.maxValue = Number(values.maxValue);
            }
          }
          if (DEFAULT_VALUE_SUPPORTED_FIELD_TYPES.includes(values.type?.value ?? "")) {
            field.defaultValue = values.defaultValue;
          }

          if (values.type?.value === "reference" && !props.isCreate) {
            field.relationshipName = values.relationshipName;
            field.relationshipFieldName = values.relationshipFieldName;
          }
          props.closeModal({
            field,
          });
        }}
      >
        <div className={styles.form}>
          <FormTextInput
            marginTop={`extra-small`}
            required={true}
            labelText={"Field Label"}
            name={`label`}
            data-testid={`form-label`}
            type="text"
            paddingInline={`none`}
          />
          <FormTextInput
            marginTop={`extra-small`}
            required={true}
            labelText={"API Name"}
            name={`api`}
            data-testid={`form-api`}
            type="text"
            paddingInline={`none`}
            disabled={!!props.field}
          />
          <Field
            name={`businessImpact`}
            children={(fieldData: FieldProps) => {
              return (
                <FormSelect<{ label: string; value: string }>
                  showClear
                  required={true}
                  data-testid={`form-type`}
                  options={FIELD_TYPE_OPTIONS}
                  name={`type`}
                  labelText={"Data Type"}
                  paddingInline={`none`}
                  isSearchable={true}
                  defaultValue={initialFormState.type}
                  value={fieldData.field.value}
                  ignoreMenuDirection={true}
                />
              );
            }}
          />

          <FormCheckBox data-testid={`form-required`} text={"Required"} name={"required"} paddingInline="none" />
          <FormCheckBox data-testid={`form-deletable`} text={"Deletable"} name={"deletable"} paddingInline="none" />

          <Field
            name={`defaultValue`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (!DEFAULT_VALUE_SUPPORTED_FIELD_TYPES.includes(fieldData.form.values.type?.value ?? "") || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Default Value"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    type={"text"}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />

          <Field
            name={`minValue`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (!MIX_MAX_SUPPORTED_FIELD_TYPES.includes(fieldData.form.values.type?.value ?? "") || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Min Value"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    type={"number"}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />

          <Field
            name={`maxValue`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (!MIX_MAX_SUPPORTED_FIELD_TYPES.includes(fieldData.form.values.type?.value ?? "") || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Max Value"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    type={"number"}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />

          <Field
            name={`relationshipName`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (fieldData.form.values.type?.value !== "reference" || props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Relationship Name"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />
          <Field
            name={`relationshipFieldName`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (fieldData.form.values.type?.value !== "reference" || props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Relationship Field Name"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />
          <Field
            name={`refApi`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (fieldData.form.values.type?.value !== "reference" || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Related To"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />
          <Field
            name={`refDependsOn`}
            children={(fieldData: FieldProps) => {
              if (fieldData.form.values.type?.value !== "reference" || !props.isCreate) {
                return null;
              }
              return (
                <MultipleInputSelect
                  labelText={"Dependencies field api name"}
                  prefix={`emails`}
                  testId={`support_case_emails`}
                  items={
                    fieldData.field.value
                      .split(",")
                      .map((item: string) => item.trim())
                      .filter(Boolean) ?? []
                  }
                  onChange={(items) => {
                    fieldData.form.setFieldValue(fieldData.field.name, items.join(","));
                  }}
                  itemType={`string`}
                  showError={true}
                />
              );
            }}
          />
          <Field
            name={`refOrderField`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (fieldData.form.values.type?.value !== "reference" || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Order api field name"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />
          <Field
            name={`refSearchField`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (fieldData.form.values.type?.value !== "reference" || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Order api field name"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />
          <Field
            name={`refLookupFieldId`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (fieldData.form.values.type?.value !== "reference" || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <Label text={"Id api field name"} marginBottom={"small"} required={true} />

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />
          <Field
            name={`refLookupFilter`}
            children={(fieldData: FieldProps<string, typeof INIT_VALUE>) => {
              if (fieldData.form.values.type?.value !== "reference" || !props.isCreate) {
                return null;
              }
              return (
                <div>
                  <div className={styles.label}>
                    <Label text={"Extra query"} marginBottom={"small"} />
                    <Tooltip
                      value={
                        <div>
                          <div>Examples:</div>
                          <div>Estado__c = ':Estado_picklist__c' AND Categoria__c = '1'</div>
                          <div>AccountId = ':selectedAccountId'</div>
                          <div>AccountId = ':selectedAccountIdOrSelectedPersonId'</div>
                          <div>Corretor__r.ID_Corretor__c = ':currentUserId' and Vinculo_ativo__c = true</div>
                        </div>
                      }
                    >
                      <span>
                        <QuestionMarkIcon />
                      </span>
                    </Tooltip>
                  </div>

                  <TextInput
                    onChange={(e) => {
                      fieldData.form.setFieldValue(fieldData.field.name, e.target.value);
                    }}
                    onClear={() => {
                      fieldData.form.setFieldValue(fieldData.field.name, "");
                    }}
                    paddingInline={`none`}
                    value={fieldData.field.value}
                  />
                </div>
              );
            }}
          />
        </div>
        <ModalButtons buttonType={`button`} onSubmit={() => formikContext.current?.submitForm()} onCancel={() => props.closeModal()} />
      </FormElement>
    </BaseModal>
  );
};
